import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Plus, Pencil, Trash2, Power, X, Tags } from "lucide-react";
import DataTable from "../../../components/DataTable";
import ConfirmationModal from "../../../components/ConfirmationModal";
import {
  listReferralVendorCategories,
  createReferralVendorCategory,
  updateReferralVendorCategory,
  deleteReferralVendorCategory,
} from "../../../services/referralAdmin";

const EMPTY = { name: "", isActive: true };

const ReferralCategoriesTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listReferralVendorCategories(false);
      setRows(Array.isArray(res) ? res : res?.items || []);
    } catch (e) {
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setModalOpen(true);
  };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, isActive: c.isActive });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Category name is required");
    setSaving(true);
    try {
      if (editing) {
        await updateReferralVendorCategory(editing.id, form);
        toast.success("Category updated");
      } else {
        await createReferralVendorCategory(form);
        toast.success("Category created");
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save category");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      await updateReferralVendorCategory(c.id, { name: c.name, isActive: !c.isActive });
      await load();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const doDelete = async () => {
    const id = confirm?.id;
    setConfirm(null);
    try {
      await deleteReferralVendorCategory(id);
      toast.success("Category deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete category");
    }
  };

  const columns = [
    { key: "name", header: "Category", width: 280, render: (r) => <span className="text-sm font-semibold text-slate-900">{r.name}</span> },
    {
      key: "status",
      header: "Status",
      width: 120,
      render: (r) => (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${r.isActive ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${r.isActive ? "bg-emerald-500" : "bg-rose-500"}`} />
          {r.isActive ? "ACTIVE" : "INACTIVE"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      width: 140,
      align: "center",
      render: (r) => (
        <div className="flex items-center justify-center gap-1.5">
          <button title="Edit" onClick={() => openEdit(r)} className="w-8 h-8 rounded-md text-slate-500 hover:text-flamingo-600 hover:bg-flamingo-50 inline-flex items-center justify-center">
            <Pencil size={15} />
          </button>
          <button title={r.isActive ? "Deactivate" : "Activate"} onClick={() => toggleActive(r)} className={`w-8 h-8 rounded-md inline-flex items-center justify-center ${r.isActive ? "text-amber-500 hover:bg-amber-50" : "text-emerald-600 hover:bg-emerald-50"}`}>
            <Power size={15} />
          </button>
          <button title="Delete" onClick={() => setConfirm({ id: r.id, name: r.name })} className="w-8 h-8 rounded-md text-rose-500 hover:bg-rose-50 inline-flex items-center justify-center">
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Broad reward categories (e.g. Electronics, Furniture, Multi-category platform).</p>
        <button onClick={openAdd} className="inline-flex items-center gap-2 rounded-xl bg-flamingo-600 hover:bg-flamingo-700 text-white text-sm font-semibold px-5 py-2.5 transition-colors">
          <Plus size={16} /> Add Category
        </button>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        minWidth={560}
        rowKey={(r, i) => r?.id ?? i}
        emptyState={
          <div className="px-6 py-12 text-center">
            <Tags size={28} className="mx-auto text-slate-300" />
            <p className="text-base font-semibold text-slate-900 mt-2">No categories yet</p>
            <p className="text-sm text-slate-500 mt-1">Add the broad categories your reward partners serve.</p>
          </div>
        }
      />

      <div className={`fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 ${modalOpen ? "" : "hidden"}`}>
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h3 className="text-base font-semibold text-slate-900">{editing ? "Edit Category" : "Add Category"}</h3>
            <button onClick={() => setModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
          </div>
          <div className="p-5">
            <label className="text-xs font-medium text-slate-600">Name <span className="text-flamingo-600">*</span></label>
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Electronics"
              className="mt-1 w-full rounded-xl border border-slate-300 py-2.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-flamingo-500/40 focus:border-flamingo-500"
            />
          </div>
          <div className="flex justify-end gap-2 px-5 py-4 bg-slate-50 border-t border-slate-200">
            <button onClick={() => setModalOpen(false)} className="rounded-xl border border-slate-300 text-slate-600 text-sm font-medium px-4 py-2.5 hover:bg-white">Cancel</button>
            <button onClick={save} disabled={saving} className="rounded-xl bg-flamingo-600 hover:bg-flamingo-700 disabled:opacity-60 text-white text-sm font-semibold px-5 py-2.5">
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={!!confirm}
        onConfirm={doDelete}
        onCancel={() => setConfirm(null)}
        message={`Delete category "${confirm?.name}"?`}
      />
    </div>
  );
};

export default ReferralCategoriesTab;
